//@ts-strict-ignore
import { useMutation } from '@apollo/client';
import React, { ReactElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import CancelDispatchInstruction from '../../graphql/mutations/sales/CancelDispatch.graphql';
import { serviceActions } from '../../state/modules/service';
import * as entities from '../../graphql/entities';
import { handleComponentQueryError, useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import SalesInvoiceFromSalesOrderMutation from 'graphql/mutations/sales/SalesInvoiceFromSalesOrderMutation.graphql';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { useFormDispatchAction } from '../../components/ActionButtons/dispatch/DispatchAction';
import { WorkspaceModalOnly } from '../../components/Workspace/WorkspaceModal';
import ModalContext from '../../components/Context/ModalContext';
import CheckboxInput from '../../components/connectedForm/CheckboxInput';
import { Analytics } from '../../mixpanel';
import {
  isDispatched,
  isDocumentsDisabled,
  isNonDraftOrder,
  isOrder,
  isPendingPriceApproval,
  isQuoteConversionAllowed,
} from '../../utils/SalesUtils';
import { ContextualMenuItemType } from '@fluentui/react';
import SalesOrderPaymentModal from '../../components/modals/SalesOrderPaymentModal';
import { IConfirmationTriggeringAction, usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import {
  ActionType,
  AlertType,
  ICancelDispatchInstructionMutationResult,
  ICancelDispatchInstructionMutationVariables,
  IMetaAction,
  ISalesInvoice,
  ISalesOrder,
  ISalesOrderInput,
  Maybe,
  OrderType,
  QuantityToPick,
  ReportType,
} from '../../graphql/schema/p4types';
import { IFormAction, IFormActions } from '../../@types/entityTypes';
import { TaskPermission } from '../../graphql/entities/diary/DiaryEvent';
import SalesOrderRefundModal from '../../components/modals/SalesOrderRefundModal';
import { useDocumentConfig, useSalesOrderConfig } from '../../utils/FetchConfig';
import { IJobMeta, useBackgroundProcess } from '../../utils/BackgroundJobs';
import DocumentModalContext from '../../components/Context/DocumentModalContext';
import Fieldset from '../../components/connectedForm/Fieldset';
import Legend from '../../components/connectedForm/Legend/Legend';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { IReduxStore } from '../../@types/reduxTypes';
import { useP4DocumentController } from '../../components/document/DocumentProvider';
import { useNotifyMaxoptraForSalesOrder } from './MaxoptraNotification';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { ALLOW_VIEW } from '../../utils/OperationPermissions';

const LazySalesOrderLineWorkspace = React.lazy(() => import('../../views/sales/SalesOrderLineWorkspace'));

export enum SalesOrderAction {
  AddPayment = 'SALES_ORDER_ADD_PAYMENT',
  AddRefund = 'SALES_ORDER_ADD_REFUND',
  AllocateStock = 'SALES_ORDER_ALLOCATE_STOCK',
  DispatchOrder = 'SALES_ORDER_MANUAL_DISPATCH',
  CancelDispatch = 'SALES_ORDER_CANCEL_DISPATCH',
  PrintPickingInstruction = 'SALES_DOCUMENTS_PICKING_INSTRUCTIONS',
  ReviewShortages = 'SALES_ORDER_REVIEW_SHORTAGES',
  Create = 'SALES_ORDERS_CREATE',
  Invoice = 'SALES_LEDGER_INVOICES_CREATE',
  LineActions = 'SALES_ORDER_LINE_ACTIONS',
  TradeCounter = 'SALES_ORDER_TRADE_COUNTER',
  PrintProductLabels = 'PRINT_PRODUCT_LABELS',
  NotifyMaxOptra = 'NOTIFY_MAXOPTRA',
  SalesOrderLabel = 'SALES_ORDER_LABELS',
}

export const quoteFormActionsForPermissions = [{ description: 'Convert', actionReference: SalesOrderAction.Create }];

export const formActionsForPermissions: IFormActions<SalesOrderAction | TaskPermission> = [
  { description: 'Add Payment', actionReference: SalesOrderAction.AddPayment },
  { description: 'Add Refund', actionReference: SalesOrderAction.AddRefund },
  { description: 'Allocate Stock', actionReference: SalesOrderAction.AllocateStock },
  { description: 'Dispatch Order', actionReference: SalesOrderAction.DispatchOrder },
  { actionReference: SalesOrderAction.NotifyMaxOptra },
  { description: 'Create Task', actionReference: TaskPermission.Create, actionIgnore: true }, //Exclude from 'sales orders' submenu. Display only against event.
  { description: 'Cancel Dispatch', actionReference: SalesOrderAction.CancelDispatch },
  { description: 'Print Picking Instructions', actionReference: SalesOrderAction.PrintPickingInstruction },
  { description: 'Review Shortages', actionReference: SalesOrderAction.ReviewShortages },
  { actionReference: SalesOrderAction.Create },
  { actionReference: SalesOrderAction.Invoice },
  { actionReference: SalesOrderAction.LineActions },
  { actionReference: SalesOrderAction.TradeCounter },
  { actionReference: SalesOrderAction.PrintProductLabels },
  { actionReference: SalesOrderAction.SalesOrderLabel },
];

const useCancelDispatchAction: () => [IFormAction<SalesOrderAction>] = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const [cancelDispatch] = useMutation<
    ICancelDispatchInstructionMutationResult,
    ICancelDispatchInstructionMutationVariables
  >(CancelDispatchInstruction, {
    onError: (data) =>
      handleComponentQueryError(data, { dispatch, entity: entities.SalesOrder, name: 'Could not cancel dispatch' }),
  });

  const runBackgroundProcess = useBackgroundProcess({
    title: 'Cancel Dispatch',
    onComplete: (job) => {
      dispatch(serviceActions.genericAlert('Dispatch', 'Dispatch cancelled', AlertType.Success));
      connectedForm.reloadData?.(true);
    },
  });

  return [
    {
      key: 'cancelDispatch',
      text: 'Cancel Dispatch',
      actionReference: SalesOrderAction.CancelDispatch,
      onClick: () => {
        cancelDispatch({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              salesOrder: normalizeVariables(connectedForm.values),
            },
          },
        }).then((result) => {
          const job = result?.data?.cancelDispatchInstruction?.result;
          runBackgroundProcess(job as IJobMeta);
        });
      },
      condition: isDispatched,
      'data-testid': 'sales-order-action-cancel-dispatch',
    },
  ];
};

const useInvoiceAction: () => [IFormAction<SalesOrderAction>, ReactNode] = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const { dispatchSimpleToast } = useP4ToastController();
  const handleQueryError = useComponentQueryErrorHandling({ name: entities.SalesInvoice.title });
  const [raiseSalesInvoiceFromSalesOrder] = useMutation(SalesInvoiceFromSalesOrderMutation, {
    onError: handleQueryError,
  });
  const [invoicePrintModalState, setInvoicePrintModalState] = useState<{
    modalOpen: boolean;
    salesInvoice?: ISalesInvoice;
  }>({ modalOpen: false });
  const [commercialInvoicePrintModalState, setCommercialInvoicePrintModalState] = useState<{
    modalOpen: boolean;
    salesInvoice?: ISalesInvoice;
  }>({ modalOpen: false });

  const isArchived = connectedForm.values?.archived;

  const onClickInvoice = useCallback(() => {
    raiseSalesInvoiceFromSalesOrder({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesOrderId: connectedForm.values?.id,
        },
      },
    }).then((result) => {
      if (result?.data && !result.errors?.message) {
        dispatchSimpleToast('Invoice success', 'Sales order has been invoiced', AlertType.Info);
        connectedForm.reloadData?.(true);

        const raisedInvoice = result?.data?.salesInvoiceFromSalesOrder?.result;
        const actions = raisedInvoice?.entityMetaData?.actions;

        if (actions) {
          actions.forEach((action: IMetaAction) => {
            switch (action.actionType) {
              case ActionType.PrintSalesInvoice:
                setInvoicePrintModalState({
                  modalOpen: true,
                  salesInvoice: raisedInvoice,
                });
                break;
              case ActionType.PrintCommercialInvoice:
                setCommercialInvoicePrintModalState({
                  modalOpen: true,
                  salesInvoice: raisedInvoice,
                });
                break;
            }
          });
        }
      }
    });
  }, [connectedForm, dispatchSimpleToast, raiseSalesInvoiceFromSalesOrder]);

  const action: IFormAction<SalesOrderAction> = {
    key: 'invoice',
    text: 'Invoice',
    onClick: onClickInvoice,
    actionReference: SalesOrderAction.Invoice,
    condition: (order: ISalesOrderInput) =>
      !order.draft &&
      order.orderType === OrderType.Order &&
      !!order.id &&
      !order.archived &&
      !isPendingPriceApproval(order),
    disabled: isArchived,
    tooltip: 'Generate a Sales Invoice for this Order',
  };

  return [
    action,
    <>
      <ModalContext.Provider
        value={{
          onClose: () => {
            setInvoicePrintModalState({ modalOpen: false });
            if (!commercialInvoicePrintModalState.modalOpen) {
              connectedForm.reloadData?.(true);
            }
          },
        }}
      >
        <DocumentModal2
          modalOpen={invoicePrintModalState.modalOpen}
          entity={entities.SalesInvoice}
          useCustomerDefaults={true}
          returnUrl={null}
          title="Print Sales Invoice"
          values={invoicePrintModalState.salesInvoice}
          contactField={null}
          overrideActionProps={() => {
            return {
              filters: [],
              actionEntities: [invoicePrintModalState.salesInvoice],
              include: true,
            };
          }}
          modalWrapper={true}
        />
      </ModalContext.Provider>
      <ModalContext.Provider
        value={{
          onClose: () => {
            setCommercialInvoicePrintModalState({ modalOpen: false });
            if (!invoicePrintModalState.modalOpen) {
              connectedForm.reloadData?.(true);
            }
          },
        }}
      >
        <DocumentModal2
          modalOpen={commercialInvoicePrintModalState.modalOpen}
          entity={entities.CommercialInvoice}
          useCustomerDefaults={true}
          returnUrl={null}
          title="Print Commercial Invoice"
          values={commercialInvoicePrintModalState.salesInvoice}
          contactField={null}
          overrideActionProps={() => {
            return {
              filters: [],
              actionEntities: [commercialInvoicePrintModalState.salesInvoice],
              include: true,
            };
          }}
          modalWrapper={true}
        />
      </ModalContext.Provider>
    </>,
  ];
};

const useNotifyMaxoptraAction = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const notifyMaxoptra = useNotifyMaxoptraForSalesOrder(connectedForm?.values?.id);
  const [performSave] = usePerformSave();

  return [
    {
      key: 'notifyMaxoptraForSalesOrderMutation',
      text: 'Notify MaxOptra',
      actionReference: SalesOrderAction.NotifyMaxOptra,
      onClick: () => {
        performSave(
          {
            submitMeta: { suppressAfterSubmit: true },
            onSaveComplete: () => {
              notifyMaxoptra();
            },
          },
          { ...connectedForm.values, confirmationPromptsEnabled: false, suppressMetaActions: true }
        );
      },
      condition: (order) => isOrder(order) && !!connectedForm?.values?.id && !isPendingPriceApproval(order),
      'data-testid': 'sales-order-notify-maxoptra',
    },
  ];
};

const usePrintLabelAction = (): [IFormAction<SalesOrderAction>, ReactNode] => {
  const connectedForm = useContext(ConnectedFormContext);
  const [printModalState, setPrintModalState] = useState<{ modalOpen: boolean; salesOrder?: Maybe<ISalesOrder> }>({
    modalOpen: false,
  });

  const onClickPrintLabel = useCallback(() => {
    setPrintModalState({
      modalOpen: true,
      salesOrder: normalizeVariables(connectedForm.values),
    });
  }, [connectedForm.values]);

  const action: IFormAction<SalesOrderAction> = {
    key: 'printLabel',
    text: 'Print Label',
    onClick: onClickPrintLabel,
    actionReference: SalesOrderAction.PrintProductLabels,
    condition: (order) => isNonDraftOrder(order) && order?.id && !isDocumentsDisabled(order),
    tooltip: 'Print Product Labels',
  };

  return [
    action,
    <ModalContext.Provider
      value={{
        onClose: () => {
          setPrintModalState({ modalOpen: false });
        },
      }}
    >
      <DocumentModal2
        modalOpen={printModalState.modalOpen}
        entity={entities.ProductLabel}
        useCustomerDefaults={true}
        returnUrl={null}
        title="Print Product Labels"
        values={printModalState.salesOrder}
        contactField={null}
        overrideActionProps={() => {
          return {
            filters: [],
            [`salesOrders`]: [printModalState.salesOrder],
            include: true,
          };
        }}
        modalWrapper={true}
        outputSelection={[
          {
            value: 'LOCAL_PRINTER',
            label: 'Local Printer',
          },
          {
            value: 'SYSTEM_PRINTER',
            label: 'System Printer',
          },
        ]}
      />
    </ModalContext.Provider>,
  ];
};

const PickingInstructionConfiguration = () => {
  const { onChange } = useContext(DocumentModalContext);
  const documentConfig = useDocumentConfig();

  const [reset, setReset] = useState(false);

  const extraConfigurationRequired =
    documentConfig?.pickingInstructionDefault?.quantityToPick === QuantityToPick.NewAllocations;

  if (!extraConfigurationRequired) return null;

  return (
    <Fieldset cols={1}>
      <Legend styles={{ root: { marginRight: 0 } }} label="Extra Configuration" />
      <CheckboxInput
        onChange={() => {
          setReset(!reset);
          if (onChange) {
            onChange('reset', !reset);
          }
        }}
        label="Reset balances"
        value={reset}
        data-testid={'sales-order-action-extra-configuration'}
      />
    </Fieldset>
  );
};

const usePrintSalesOrderLabelsAction: () => [IFormAction<SalesOrderAction>] = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const [printModalState, setPrintModalState] = useState<{ modalOpen: boolean; salesOrder?: Maybe<ISalesOrder> }>({
    modalOpen: false,
  });

  const enableDocumentCarousel = useSelector((store: IReduxStore) => {
    return store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false;
  });

  const { printDocumentSingle } = useP4DocumentController();

  const printLabelAction: IFormAction<SalesOrderAction> = {
    key: 'printSalesOrderLabel',
    text: 'Print Sales Order Labels',
    condition: (salesOrder: ISalesOrder) => !salesOrder?.archived && !salesOrder?.draft,
    actionReference: SalesOrderAction.SalesOrderLabel,
    onClick: () => {
      if (enableDocumentCarousel) {
        printDocumentSingle(ReportType.SalesOrderLabel, connectedForm.values.id);
      } else {
        setPrintModalState({
          modalOpen: true,
          salesOrder: normalizeVariables(connectedForm.values),
        });
      }
    },
    'data-testid': 'sales-order-action-print-picking-instruction',
  };

  return [printLabelAction];
};

const usePrintPickingInstructionAction: () => [IFormAction<SalesOrderAction>, ReactElement] = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const [printModalState, setPrintModalState] = useState<{ modalOpen: boolean; salesOrder?: Maybe<ISalesOrder> }>({
    modalOpen: false,
  });

  const enableDocumentCarousel = useSelector((store: IReduxStore) => {
    return store.app.fullUserDetails?.flags?.documentCarouselEnabled ?? false;
  });

  const { printDocumentSingle } = useP4DocumentController();

  const printPickAction: IFormAction<SalesOrderAction> = {
    key: 'printPickingInstruction',
    text: 'Print Picking Instruction',
    condition: (salesOrder: ISalesOrder) =>
      !salesOrder?.archived && !!salesOrder?.pickRequired && !salesOrder?.draft && !isDocumentsDisabled(salesOrder),
    actionReference: SalesOrderAction.PrintPickingInstruction,
    onClick: () => {
      if (enableDocumentCarousel) {
        printDocumentSingle(ReportType.SalesPickingList, connectedForm.values.id);
      } else {
        setPrintModalState({
          modalOpen: true,
          salesOrder: normalizeVariables(connectedForm.values),
        });
      }
    },
    'data-testid': 'sales-order-action-print-picking-instruction',
  };

  return [
    printPickAction,
    <ModalContext.Provider
      value={{
        onClose: () => {
          setPrintModalState({ modalOpen: false });
          connectedForm.reloadData?.(true); //Reload to update 'pickRequired' flag.
        },
      }}
    >
      <DocumentModal2
        modalOpen={printModalState.modalOpen}
        entity={entities.PickingInstruction}
        useCustomerDefaults={true}
        returnUrl={null}
        title="Print Picking Instruction"
        values={printModalState.salesOrder}
        contactField={null}
        overrideActionProps={() => {
          return {
            filters: [],
            [`salesOrders`]: [printModalState.salesOrder],
            include: true,
          };
        }}
        modalWrapper={true}
        generateSingle={true}
        allowEntityEmail={false}
        onComplete={() => {
          //Reload to update 'pickRequired' flag
          connectedForm.reloadData?.(true);
        }}
      >
        <PickingInstructionConfiguration />
      </DocumentModal2>
    </ModalContext.Provider>,
  ];
};

//Action to display a line workspaceActionsForPermissions modal filtered to this orders' lines.
const useLineActions: () => [IFormAction<SalesOrderAction>, JSX.Element] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const predefinedFilters = [
    {
      filterOperation: 'EQUALS',
      filterParameters: {
        field: 'salesOrder.id',
        value: connectedForm.values?.id,
      },
    },
  ];

  const lineActionsOnClick = useCallback(() => {
    if (connectedForm.dirty) {
      dispatch(
        serviceActions.genericAlert(
          'Unsaved changes',
          'Line actions cannot be performed while there are unsaved changes on the form, please save (or refresh) the page before processing line actions.',
          AlertType.Warning
        )
      );
    } else {
      setModalOpen(true);
    }
  }, [connectedForm.dirty, dispatch]);

  return [
    {
      key: 'lineActions',
      text: 'Line Actions',
      condition: () => !!connectedForm.values?.id && !connectedForm.values?.draft,
      actionReference: SalesOrderAction.LineActions,
      onClick: lineActionsOnClick,
      'data-testid': 'sales-order-action-line-actions',
    } as IFormAction<SalesOrderAction>,
    <WorkspaceModalOnly isOpen={modalOpen}>
      <ModalContext.Provider
        value={{
          onClose: () => {
            setModalOpen(false);
            connectedForm.reloadData?.(true);
          },
        }}
      >
        <LazySalesOrderLineWorkspace
          viewScreenName={'/Customers/Return/'}
          predefinedFilters={predefinedFilters}
          disableViewController={true}
          includeHeader={true}
          permittedOperations={ALLOW_VIEW}
          noForm={true}
          noEdit={true}
          embeddedView={true}
          onBack={() => setModalOpen(false)}
          editInNewTab={true}
          enableRowSelect={true}
          fieldNames={['returned']}
        />
      </ModalContext.Provider>
    </WorkspaceModalOnly>,
  ];
};

//Action to open the current Sales Order in the trade counter.
const useTradeCounterAction: (performSave: IConfirmationTriggeringAction) => [IFormAction<SalesOrderAction>] = (
  performSave
) => {
  const connectedForm = useContext(ConnectedFormContext);
  const history = useHistory();

  return [
    {
      key: 'tradeCounter',
      text: 'Trade Counter',
      actionReference: SalesOrderAction.TradeCounter,
      onClick: () => {
        //Trigger a save first so we jump over with up-to-date details.
        performSave({
          submitMeta: { suppressAfterSubmit: true, suppressPostSave: true },
          onSaveComplete: ({ result: salesOrder }) => {
            history.replace({
              pathname: `/Sales/TradeCounter/Details/New`,
              state: {
                salesOrderId: salesOrder?.id,
                previousLocation: 'back',
              },
            });
          },
        });
      },
      condition: () =>
        !connectedForm.values?.archived &&
        connectedForm.values?.orderType === 'ORDER' &&
        !connectedForm.values?.customer?.foreignCurrency &&
        !isPendingPriceApproval(connectedForm.values as ISalesOrder),
      'data-testid': 'sales-order-action-trade-counter',
    },
  ];
};

const usePaymentAction: () => [IFormAction<SalesOrderAction>, JSX.Element] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);

  return [
    {
      key: 'addPayment',
      text: 'Add Payment',
      condition: (order) => isOrder(order) && !!connectedForm?.values?.id && !isPendingPriceApproval(order),
      actionReference: SalesOrderAction.AddPayment,
      onClick: () => {
        setModalOpen(true);
      },
      'data-testid': 'sales-order-action-add-payment',
    } as IFormAction<SalesOrderAction>,
    <ModalContext.Provider
      value={{
        onClose: () => {
          setModalOpen(false);
        },
      }}
    >
      <SalesOrderPaymentModal
        isOpen={modalOpen}
        orderId={connectedForm.values?.id}
        billingAddress={connectedForm.values?.customer?.address}
        emailAddress={connectedForm.values?.customer?.email}
        warehouseId={connectedForm.values?.warehouse?.id}
        paymentMethodDefault={connectedForm.values?.paymentMethod}
      />
    </ModalContext.Provider>,
  ];
};

const useRefundAction: () => [IFormAction<SalesOrderAction>, JSX.Element] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);

  return [
    {
      key: 'addRefund',
      text: 'Add Refund',
      condition: (order) => isNonDraftOrder(order) && !!connectedForm?.values?.id && !isPendingPriceApproval(order),
      actionReference: SalesOrderAction.AddRefund,
      onClick: () => setModalOpen(true),
      'data-testid': 'sales-order-action-add-refund',
    } as IFormAction<SalesOrderAction>,
    <ModalContext.Provider
      value={{
        onClose: () => {
          setModalOpen(false);
        },
      }}
    >
      <SalesOrderRefundModal isOpen={modalOpen} />
    </ModalContext.Provider>,
  ];
};

const useConvertOrderAction: () => IFormAction<SalesOrderAction> = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const history = useHistory();

  const onClick = () => {
    history.push({
      pathname: '/Sales/Order/Details/New',
      state: {
        duplicateId: connectedForm.values?.id,
        defaultValues: [
          {
            field: 'orderType',
            value: 'ORDER',
          },
        ],
        previousLocation: history.location?.pathname || 'back',
      },
    });
  };

  return {
    key: 'convertOrder',
    text: 'Convert',
    individualButton: false, //Not really necessary - If using key/text already defining as a fluent button...
    condition: isQuoteConversionAllowed,
    actionReference: SalesOrderAction.Create,
    onClick: onClick,
    'data-testid': 'sales-order-action-convert',
  };
};

const useGeneratePurchaseRequirementAction: () => IFormAction<SalesOrderAction> = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const orderConfig = useSalesOrderConfig();

  const onClick = () => {
    Analytics.track('Review Shortages Clicked');

    Promise.all([dispatch(connectedForm.change?.('reviewShortagesLater', true))]).then(() => {
      const { submitOptions } = connectedForm;
      if (submitOptions) {
        const [, dispatchSubmitOptions] = submitOptions;
        dispatchSubmitOptions?.({ type: SubmitOption.Continue });
      }

      connectedForm.submit?.(); //Save should bring up shortage review because we've tagged 'reviewShortagesLater' as true.
    });
  };

  return {
    key: 'reviewShortages',
    text: 'Review Shortages',
    onClick: onClick,
    condition: (order: ISalesOrderInput) =>
      isNonDraftOrder(order) &&
      (orderConfig?.allowAllocationForOrderWithUnapprovedPrices || !isPendingPriceApproval(order)),
    actionReference: SalesOrderAction.ReviewShortages,
    individualButton: !connectedForm.values?.id, //If we're creating a new order, display the review shortages button more prominently.
    'data-testid': 'sales-order-action-review-shortages',
  };
};

//Rewrite formActions function as a hook so we can access other hooks rather than excessive reliant on arguments.
export const useSalesOrderFormActions: () => [Array<IFormAction<SalesOrderAction | TaskPermission>>, ReactElement] =
  () => {
    const connectedForm = useContext(ConnectedFormContext);
    const convertOrderAction = useConvertOrderAction();
    const history = useHistory();

    const reviewShortagesAction = useGeneratePurchaseRequirementAction();

    const [performSave, confirmationModals] = usePerformSave();

    const [dispatchActionButton, printModals] = useFormDispatchAction(performSave);
    const [notifyMaxOptra] = useNotifyMaxoptraAction();
    const [lineActions, lineActionsModals] = useLineActions();
    const [paymentAction, paymentModals] = usePaymentAction();
    const [refundAction, refundModals] = useRefundAction();
    const [tradeCounterAction] = useTradeCounterAction(performSave);
    const [cancelDispatchAction] = useCancelDispatchAction();
    const [printPickingInstructionAction, printPickingModals] = usePrintPickingInstructionAction();
    const [printSalesOrderLabelAction] = usePrintSalesOrderLabelsAction();

    const orderConfig = useSalesOrderConfig();
    const [invoiceAction, invoiceActionModals] = useInvoiceAction();
    const [printLabelAction, printLabelModals] = usePrintLabelAction();

    useEffect(() => {
      // save sales order after payment
      if (connectedForm.values?.newPaymentLines?.length > 0) {
        // call afterSubmit to set navigation only if this is a new order
        performSave({ submitMeta: { suppressAfterSubmit: !!connectedForm.values?.id } });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectedForm.values?.newPaymentLines]);

    const salesOrderActions: Array<IFormAction<SalesOrderAction | TaskPermission>> = [
      convertOrderAction,
      reviewShortagesAction,
      {
        key: 'allocateStock',
        actionReference: SalesOrderAction.AllocateStock,
        text: 'Allocate Stock',
        onClick: () => {
          history.push({
            pathname: `${entities.ProductAllocation.formUrl}${connectedForm.values?.id}`,
            state: { previousLocation: history.location?.pathname || 'back' },
          });
        },
        condition: (order) =>
          isOrder(order) &&
          !!connectedForm?.values?.id &&
          (orderConfig?.allowAllocationForOrderWithUnapprovedPrices || !isPendingPriceApproval(order)),
        'data-testid': 'sales-order-action-allocate-stock',
      },
      {
        key: 'createTask',
        actionReference: TaskPermission.Create,
        text: 'Create Task',
        onClick: () => {
          history.push({
            pathname: `${entities.DiaryEvent.formUrl}New/SalesOrder/${connectedForm.values?.id}`,
            state: { previousLocation: history.location?.pathname || 'back' },
          });
        },
        condition: () => !!connectedForm.values?.id,
        'data-testid': 'sales-order-action-create-task',
      },
      dispatchActionButton as IFormAction<SalesOrderAction>, //Cast because hook also can validly return IWorkspaceAction.
      cancelDispatchAction,
      invoiceAction,
      notifyMaxOptra,
      lineActions,
      tradeCounterAction,
      paymentAction,
      refundAction,
      {
        key: 'reporting',
        itemType: ContextualMenuItemType.Header,
        text: 'Reports',
        ignorePermissions: true, //Form actions are filtered out if no permission config is set, so need this to bypass the check.
        condition: () => !connectedForm.values?.archived,
      } as any, //Special case for injecting subheader
      printLabelAction,
      printPickingInstructionAction,
      printSalesOrderLabelAction,
    ];

    return [
      salesOrderActions,
      <>
        {printModals}
        {lineActionsModals}
        {paymentModals}
        {refundModals}
        {confirmationModals}
        {printPickingModals}
        {invoiceActionModals}
        {printLabelModals}
      </>,
    ];
  };
